import { useState } from "react"
import { Plus, Heart, MapPin, Bookmark } from "lucide-react"
import { Link } from "@tanstack/react-router"
import { 
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  useRepository,
  SignInPrompt
} from "@city-sage/aurum-avis-labs-components"
import { useAuth } from "@city-sage/aurum-avis-labs-components"
import { ItineraryCard } from "../ItineraryCard"
import { ItineraryCardSkeleton } from "../ItineraryCardSkeleton"
import CompactHeader from "../CompactHeader"
import { useTranslation } from "react-i18next"
import { ItineraryVisibilityEnum, ReactionReactableTypeEnum, Itinerary } from "@city-sage/core-client"
import { QueryKeys } from "../../lib/QueryKeys"
import { useQuery, useInfiniteQuery } from "@tanstack/react-query"
import { useIsAuthenticated } from "@azure/msal-react"

export default function MyItinerariesPage() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState("myItineraries")
  const isAuthenticated  = useIsAuthenticated()

  const { api } = useRepository()
  const {data: account} = useQuery({
    queryKey: QueryKeys.account.details(),
    queryFn: () => api.getMyAccount(),
    enabled: !!isAuthenticated,
  })

 

  // Fetch user's created itineraries with infinite scroll
  const {
    data: myItinerariesData,
    isLoading: myIsLoading,
    isError: myIsError,
    error: myError,
    fetchNextPage: fetchNextMyItineraries,
    hasNextPage: hasNextMyItineraries,
    isFetchingNextPage: isFetchingNextMyItineraries
  } = useInfiniteQuery({
    queryKey: QueryKeys.itinerary.my(),
    queryFn: async ({ pageParam = 1 }) => {
      const res = await api.findAllItineraryRaw({ 
        limit: 12,
        page: pageParam,
        filter: JSON.stringify({
          accountId: account?.id,
        }),
        sort: JSON.stringify(['createdAt', "DESC"])
      })
      return await res.value()
    },
    enabled: !!isAuthenticated,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || !Array.isArray(lastPage) || lastPage.length < 12) return undefined
      return (allPages?.length ?? 0) + 1
    },
    initialPageParam: 1,
  })

  // Fetch saved itineraries with infinite scroll
  const {
    data: savedItinerariesData,
    isLoading: savedIsLoading,
    isError: savedIsError,
    error: savedError,
    fetchNextPage: fetchNextSavedItineraries,
    hasNextPage: hasNextSavedItineraries,
    isFetchingNextPage: isFetchingNextSavedItineraries
  } = useInfiniteQuery({
    queryKey: QueryKeys.itinerary.savedItineraries(),
    queryFn: async ({ pageParam = 1 }) => {
      const reactions = await api.findAllReaction({
        filter: JSON.stringify({
          reactableType: ReactionReactableTypeEnum.Itinerary,
        }),
        sort: JSON.stringify(['createdAt', "DESC"]),
        limit: 12,
        page: pageParam
      })
      
      const itineraries = await Promise.all(reactions.map(async (reaction) => {
        const itinerary = await api.findOneItinerary({
          id: reaction.reactableId,
        })
        return itinerary
      }))
      
      return itineraries
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || !Array.isArray(lastPage) || lastPage.length < 12) return undefined
      return (allPages?.length ?? 0) + 1
    },
    initialPageParam: 1,
    enabled: !!isAuthenticated,
  })

  // Flatten infinite query results
  const myItineraries = myItinerariesData?.pages?.flat() || []
  const savedItineraries = savedItinerariesData?.pages?.flat() || []

  // Get current tab data
  const currentItineraries = activeTab === "myItineraries" ? myItineraries : savedItineraries
  const currentIsLoading = activeTab === "myItineraries" ? myIsLoading : savedIsLoading
  const currentIsError = activeTab === "myItineraries" ? myIsError : savedIsError
  const currentError = activeTab === "myItineraries" ? myError : savedError
  const currentFetchNextPage = activeTab === "myItineraries" ? fetchNextMyItineraries : fetchNextSavedItineraries
  const currentHasNextPage = activeTab === "myItineraries" ? hasNextMyItineraries : hasNextSavedItineraries
  const currentIsFetchingNextPage = activeTab === "myItineraries" ? isFetchingNextMyItineraries : isFetchingNextSavedItineraries

  const myItinerariesCount = myItineraries.length
  const savedItinerariesCount = savedItineraries.length

 // Show sign-in prompt if user is not authenticated
 if (!isAuthenticated) {
  return (
    <div className="min-h-dvh bg-gray-50">
      <CompactHeader
        icon={MapPin}
        title={t('myItineraries.title')}
        subtitle={t('myItineraries.manageYourItineraries')}
      />
      <div className="container mx-auto px-4 py-6">
        <SignInPrompt
          title={t('myItineraries.signInRequired.title', 'Sign in to view your itineraries')}
          description={t('myItineraries.signInRequired.description', 'Create an account or sign in to manage your travel itineraries and saved trips.')}
          icon={<MapPin className="h-8 w-8 text-blue-500" />}
        />
      </div>
    </div>
  )
}

  if (currentIsError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <p className="text-destructive">{t('myItineraries.error')}</p>
        <p className="text-sm text-muted-foreground">{currentError?.message}</p>
      </div>
    )
  }

  return (
    <div className="min-h-dvh bg-gray-50">
      {/* Header */}
      <CompactHeader
        icon={MapPin}
        title={t('myItineraries.title')}
        subtitle={t('myItineraries.manageYourItineraries')}
      />

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Create Button */}
          <Link to="/itineraries/create">
            <Button className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              {t('myItineraries.createNew')}
            </Button>
          </Link>

        {/* Tabs for filtering */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="myItineraries">
              <MapPin className="h-4 w-4 mr-2" />
              {t('myItineraries.tabs.myItineraries')} ({myItinerariesCount})
            </TabsTrigger>
            <TabsTrigger value="saved">
              <Heart className="h-4 w-4 mr-2" />
              {t('myItineraries.tabs.saved')} ({savedItinerariesCount})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="myItineraries" className="mt-6">
            {currentIsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {Array.from({ length: 8 }).map((_, index) => (
                  <ItineraryCardSkeleton key={index} />
                ))}
              </div>
            ) : currentItineraries.length === 0 ? (
              <Card className="p-8">
                <CardContent className="flex flex-col items-center justify-center space-y-4 text-center">
                  <MapPin className="h-12 w-12 text-muted-foreground" />
                  <CardTitle>{t('myItineraries.empty.title')}</CardTitle>
                  <CardDescription className="max-w-md">
                    {t('myItineraries.empty.descriptionMyItineraries')}
                  </CardDescription>
                  <Link to="/itineraries/create">
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      {t('myItineraries.createFirst')}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {currentItineraries.map((itinerary: Itinerary) => (
                    <ItineraryCard
                      key={itinerary.id}
                      itinerary={itinerary}
                      link={`/itineraries/${itinerary.id}`}
                      images={[]}
                      showStateBadge={true}
                    />
                  ))}
                </div>

                {/* Load More Button */}
                {currentHasNextPage && (
                  <div className="flex justify-center pt-8">
                    <Button
                      variant="outline"
                      onClick={() => currentFetchNextPage()}
                      disabled={currentIsFetchingNextPage}
                    >
                      {currentIsFetchingNextPage ? t('common.loading') : t('common.loadMore')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="saved" className="mt-6">
            {currentIsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {Array.from({ length: 8 }).map((_, index) => (
                  <ItineraryCardSkeleton key={index} />
                ))}
              </div>
            ) : currentItineraries.length === 0 ? (
              <Card className="p-8">
                <CardContent className="flex flex-col items-center justify-center space-y-4 text-center">
                  <Bookmark className="h-12 w-12 text-muted-foreground" />
                  <CardTitle>{t('myItineraries.empty.savedTitle')}</CardTitle>
                  <CardDescription className="max-w-md">
                    {t('myItineraries.empty.descriptionSaved')}
                  </CardDescription>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {currentItineraries.map((itinerary: Itinerary) => (
                    <ItineraryCard
                      key={itinerary.id}
                      itinerary={itinerary}
                      link={`/itineraries/${itinerary.id}`}
                      images={[]}
                      showStateBadge={true}
                    />
                  ))}
                </div>

                {/* Load More Button */}
                {currentHasNextPage && (
                  <div className="flex justify-center pt-8">
                    <Button
                      variant="outline"
                      onClick={() => currentFetchNextPage()}
                      disabled={currentIsFetchingNextPage}
                    >
                      {currentIsFetchingNextPage ? t('common.loading') : t('common.loadMore')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
} 